import { Dialog, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CloseIcon from "@mui/icons-material/Close";
import { useMemo } from "react";

import { GapBreakpointModule, TextBreakpointModule, ButtonLinkModule } from "@/src/component/partial/__module__";
import { FormHooks } from "@/src/hook";
import { StrapiComponentType } from "@/src/type";
import { LeadBody } from "@/src/type/contact";

import { Form } from "./component";

import theme from "@/styles/scss/theme.module.scss";
import styles from "./BuildDynamicForm.module.scss";

interface BuildDynamicFormProps {
  form: StrapiComponentType.Section.DynamicForm;
  formId: string;
  disableAnimatedLabel?: boolean;
  metadata?: Record<string, string>;
  isDark?: boolean;
  onSuccess?: (leadBody: LeadBody) => void;
}

const BuildDynamicForm: React.FC<BuildDynamicFormProps> = ({ form, formId, disableAnimatedLabel, metadata, isDark, onSuccess }) => {
  const { submissionDisclaimer, submissionDisclaimerLinks, destinations: destinationsStrapi } = form;
  const destinations = destinationsStrapi?.data.map((destination) => destination.attributes);
  const { currentStepIndex, crmLinkId, isLoading, openMultiStepModal, setOpenMultiStepModal, submitForm } = FormHooks.useDynamicForm({
    metadata,
    onSuccess,
    form,
    destinations,
  });
  const step1 = form?.steps?.[0];

  const onSubmit = (formData: Record<string, any>, currentStep: number) => {
    submitForm({ formData, currentStep, trackingEventOverrides: step1.trackingEvents });
  };

  const parsedDisclaimer = useMemo(() => {
    if (!submissionDisclaimer) return null;

    return submissionDisclaimer
      .split(/(\$\{[^}]+\})/g) // Split by placeholders with ${...} format, keeping delimiters
      .map((part, index) => {
        if (!part.startsWith("${") || !part.endsWith("}")) {
          return part || null;
        }

        const placeholderName = part.slice(2, -1); // Remove ${ and }
        const matchingLink = (submissionDisclaimerLinks ?? []).find((link) => link.name === placeholderName || link.label === placeholderName);

        return matchingLink ? (
          <ButtonLinkModule
            className={styles.disclaimerLink}
            key={`disclaimer-link-${index}`}
            buttonLink={{
              label: matchingLink.label,
              href: matchingLink.href,
              isExternal: matchingLink.isExternal,
              target: matchingLink.target === "_blank" ? "_blank" : "_self",
              name: matchingLink.name,
              variant: "text",
              color: isDark ? "primary" : "secondary",
            }}
          />
        ) : null;
      })
      .filter(Boolean);
  }, [submissionDisclaimer, submissionDisclaimerLinks, isDark]);

  if (!step1) {
    return <></>;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <GapBreakpointModule
        id={`${formId}_container`}
        gapBreakpoints={form.gapBreakpoints}
        className={styles.container}
      >
        {currentStepIndex > 0 && (
          <Dialog
            open={openMultiStepModal}
            onClose={() => setOpenMultiStepModal(false)}
            sx={{
              "& .MuiModal-backdrop": {
                backdropFilter: "blur(3px)",
              },
            }}
          >
            <div className={styles.dialogContainer}>
              <div className={styles.card}>
                <div
                  className={styles.dialogExit}
                  onClick={() => setOpenMultiStepModal(false)}
                >
                  <CloseIcon
                    fontSize="large"
                    sx={{ color: theme.primaryMain }}
                  />
                </div>
                <div className={styles.stepperContainer}>
                  <Stepper
                    activeStep={currentStepIndex}
                    alternativeLabel
                  >
                    {form?.steps?.map((step) => (
                      <Step key={step.id}>
                        <StepLabel>{step.stepperLabel ?? step?.title?.text}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                  <div className={styles.formContainer}>
                    <div className={styles.formContainerInner}>
                      <div className={styles.textContainer}>
                        <TextBreakpointModule
                          id={`form_title_${form.id}`}
                          text={form?.steps?.[currentStepIndex]?.title}
                          className={styles.title}
                        />
                        <TextBreakpointModule
                          id={`form_desc_${form.id}`}
                          text={form?.steps?.[currentStepIndex]?.subtitle}
                          className={styles.description}
                        />
                      </div>
                      <Form
                        form={form}
                        formId={formId}
                        isDark={true}
                        step={form?.steps?.[currentStepIndex]}
                        crmLinkId={crmLinkId}
                        onSubmit={onSubmit}
                        showNextButton={currentStepIndex !== form?.steps?.length - 1}
                        lastStep={currentStepIndex === form?.steps?.length - 1}
                        currentStep={currentStepIndex}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Dialog>
        )}
        <Form
          form={form}
          formId={formId}
          isDark={isDark}
          disableAnimatedLabel={disableAnimatedLabel}
          step={step1}
          crmLinkId={crmLinkId}
          onSubmit={onSubmit}
          isLoading={isLoading}
        />
        <Typography
          className={styles.disclaimerText}
          color={isDark ? theme.primaryMain : theme.secondaryMain}
        >
          {parsedDisclaimer}
        </Typography>
      </GapBreakpointModule>
    </LocalizationProvider>
  );
};

export default BuildDynamicForm;
